import { useMemo, useState } from 'react'
import { Award, ChevronDown, ChevronUp, PackageCheck, Trophy } from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  MoneyValue,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import type { AwardRequest, AwardsPerformance, AwardTier } from '../api/dashboard.contract'
import {
  awardActiveGrid,
  awardFutureGrid,
  awardFutureToggle,
  awardHero,
  awardHeroHeader,
  awardHeroMeta,
  awardHeroName,
  awardHeroTitle,
  awardItem,
  awardPill,
  awardPillIcon,
  awardSection,
  awardSectionTitle,
  goalProgressFill,
  productMeta,
  progressTrack,
  sectionHeader,
  sectionSubtitle,
  sectionTitle,
} from './dashboard.css'
import { RedeemAwardDrawer } from './RedeemAwardDrawer'

interface AwardsStripProps {
  performance: AwardsPerformance | undefined
  requests: AwardRequest[]
  isPending: boolean
  isError: boolean
  isRequesting: boolean
  onRetry: () => void
  onRequest: (metaId: string, endereco: Record<string, unknown>) => Promise<void>
}

function pct(total: string, target: string): number {
  const denominator = Number(target)
  if (denominator <= 0) {
    return 0
  }
  return Math.min(100, (Number(total) / denominator) * 100)
}

function tierStatus(tier: AwardTier): { label: string; tone: 'success' | 'awaiting' | 'neutral' } {
  if (tier.requestStatus === 'ATENDIDO') return { label: 'Atendido', tone: 'success' }
  if (tier.requestStatus === 'PENDENTE') return { label: 'Solicitado', tone: 'awaiting' }
  if (tier.reached) return { label: 'Liberado', tone: 'success' }
  return { label: 'Meta futura', tone: 'neutral' }
}

export function AwardsStrip({
  performance,
  requests,
  isPending,
  isError,
  isRequesting,
  onRetry,
  onRequest,
}: AwardsStripProps) {
  const [selectedTier, setSelectedTier] = useState<AwardTier | null>(null)
  const [showFuture, setShowFuture] = useState(false)
  const requestedIds = useMemo(() => new Set(requests.map((request) => request.metaId)), [requests])

  if (isPending) {
    return (
      <Card padding="md">
        <Skeleton width="100%" height={180} />
      </Card>
    )
  }

  if (isError || !performance) {
    return (
      <Card padding="md">
        <ErrorState onRetry={onRetry} />
      </Card>
    )
  }

  const nextTier = performance.nextTier
  const reachedTiers = performance.tiers.filter((tier) => tier.reached)
  const futureTiers = performance.tiers.filter((tier) => !tier.reached)
  const progress = nextTier ? pct(performance.total.amount, nextTier.target.amount) : 100
  const heroTier = nextTier ?? reachedTiers[reachedTiers.length - 1] ?? null
  const canRequestHero = heroTier?.reached === true && !requestedIds.has(heroTier.id)

  return (
    <Card padding="md" role="region" aria-label="Conquistas e premiações">
      <div className={sectionHeader}>
        <div>
          <span className={sectionTitle}>Conquistas e premiações</span>
          <div className={sectionSubtitle}>
            Metas em reais, com o acumulado das vendas elegíveis. O período do painel não altera seu
            progresso.
          </div>
        </div>
      </div>

      {heroTier ? (
        <div className={awardHero}>
          <div className={awardHeroHeader}>
            <div className={awardHeroTitle}>
              <span className={sectionSubtitle}>
                {nextTier ? 'Próxima conquista' : 'Maior conquista atingida'}
              </span>
              <span className={awardHeroName}>{heroTier.nome}</span>
              <div className={awardHeroMeta}>
                <span>
                  Total acumulado em reais{' '}
                  <MoneyValue
                    amount={performance.total.amount}
                    currency={performance.total.currency}
                    size="sm"
                  />
                </span>
                <span>
                  Alvo{' '}
                  <MoneyValue
                    amount={heroTier.target.amount}
                    currency={heroTier.target.currency}
                    size="sm"
                  />
                </span>
                {nextTier ? (
                  <span>
                    Faltam{' '}
                    <MoneyValue
                      amount={nextTier.missing.amount}
                      currency={nextTier.missing.currency}
                      size="sm"
                    />
                  </span>
                ) : null}
              </div>
            </div>
            {canRequestHero ? (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<PackageCheck size={15} />}
                onClick={() => setSelectedTier(heroTier)}
              >
                Solicitar prêmio
              </Button>
            ) : (
              <StatusBadge
                tone={tierStatus(heroTier).tone}
                label={tierStatus(heroTier).label}
                size="sm"
              />
            )}
          </div>
          <div className={progressTrack} aria-hidden="true">
            <div className={goalProgressFill} style={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<Award size={22} />}
          title="Sem faixas configuradas"
          description="As faixas de premiação aparecem aqui quando estiverem disponíveis."
        />
      )}

      {reachedTiers.length > 0 ? (
        <div className={awardSection}>
          <span className={awardSectionTitle}>Conquistadas</span>
          <div className={awardActiveGrid}>
            {reachedTiers.map((tier) => {
              const status = tierStatus(tier)
              const canRequest = !requestedIds.has(tier.id)
              return (
                <div key={tier.id} className={awardPill}>
                  <span className={awardPillIcon}>
                    <Trophy size={15} />
                  </span>
                  <div>
                    <strong>{tier.nome}</strong>
                    <div className={productMeta}>
                      <MoneyValue
                        amount={tier.target.amount}
                        currency={tier.target.currency}
                        size="sm"
                      />
                    </div>
                  </div>
                  {canRequest ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<PackageCheck size={14} />}
                      onClick={() => setSelectedTier(tier)}
                    >
                      Solicitar
                    </Button>
                  ) : (
                    <StatusBadge tone={status.tone} label={status.label} size="sm" />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {futureTiers.length > 0 ? (
        <div className={awardSection}>
          <Button
            className={awardFutureToggle}
            variant="text"
            size="sm"
            rightIcon={showFuture ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            onClick={() => setShowFuture((current) => !current)}
          >
            Ver todas as faixas
          </Button>
          {showFuture ? (
            <div className={awardFutureGrid}>
              {futureTiers.map((tier) => (
                <div key={tier.id} className={awardItem({ reached: false })}>
                  <StatusBadge tone="neutral" label="Meta futura" size="sm" />
                  <strong>{tier.nome}</strong>
                  <span className={productMeta}>
                    <MoneyValue
                      amount={tier.target.amount}
                      currency={tier.target.currency}
                      size="sm"
                    />
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <RedeemAwardDrawer
        tier={selectedTier}
        open={selectedTier !== null}
        isRequesting={isRequesting}
        onClose={() => setSelectedTier(null)}
        onRequest={onRequest}
      />
    </Card>
  )
}
